import {
  Button,
  TextField,
  Checkbox,
  AppHeader,
  Typography,
  Alert,
  Form,
  AddressSearch,
  Box,
  useAddressSearchValidator,
  Select,
  MenuItem,
  Grid,
} from '@ace-clublabs/shared-component-library';
import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { Address } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { ConfirmZipCodeModal } from '../components/ConfirmZipCodeModal';
import { getNextPage } from '../utils/getNextPage';
import { UtagType } from '../utils/UtagHelper';
import { FlowType } from '@ace-clublabs/common-membership-api-types';
import { EmployeeInvalidClubModal } from '../components/EmployeeInvalidClubModal';
import { getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { getAddressPageTitle } from '../utils/stringHelper';

export interface ICorrectedResidence {
  address?: Address;
}

interface IAutoCompleteAddressPageProps {
  addressType: 'residence' | 'mailing';
}

export const AutoCompleteAddressPage: React.FC<IAutoCompleteAddressPageProps> = ({ addressType }) => {
  const {
    sessionData,
    submitMembershipForm,
    loadingProvider,
    loadingInfo,
    membershipInfo,
    refreshSession,
    sendTag,
    updateMembershipForm,
    getNewBusinessCost,
    handleZipCodeChange,
  } = useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);
  const [serverValidationErrors, setServerValidationErrors] = useState({ hasError: false, error: '' });
  const [showConfirmZipCodeModal, setShowConfirmZipCodeModal] = useState(false);
  const [isOOT, setIsOOT] = useState(false);
  const addressExist =
    addressType === 'residence'
      ? formData.residenceAddress && formData.residenceAddress.streetAddress !== ''
      : formData.mailingAddress && formData.mailingAddress.streetAddress !== '';

  const [addressSelected, setAddressSelected] = useState(addressExist);
  const addressValidator = useAddressSearchValidator({ required: !addressSelected, requireCounty: false });
  const buildAddressSearchValue = (address: Address | undefined) => {
    if (!address?.streetAddress || !address?.city || !address?.state || !address?.zipCode) {
      return null;
    }

    const secondaryText = `${address.city}, ${address.state} ${address.zipCode}`.trim();
    return {
      id: address.googleAddressId ?? '',
      shortDisplay: {
        primaryText: address.streetAddress,
        secondaryText,
      },
      longDisplay: {
        primaryText: address.streetAddress,
        secondaryText,
      },
      shortParts: {
        street: address.streetAddress,
        city: address.city,
        state: address.state,
        zip: address.zipCode,
      },
      longParts: {
        street: address.streetAddress,
        city: address.city,
        state: address.state,
        zip: address.zipCode,
      },
    };
  };
  const isGettingResidenceAddress = addressType === 'residence';
  const currentPage = addressType === 'residence' ? Pages.ResidenceAddressPage : Pages.MailingAddressPage;
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;
  const pageNameTag = `${isGiftFlow ? 'membership:gift:recipient address' : 'contact information'}`;

  const differentAddressText = isGiftFlow
    ? 'Recipient has a different mailing address'
    : 'I have a different mailing address';
  // set and modified when mailing address checkbox is clicked so new form renders without internal state
  const [formKey, setFormKey] = useState(new Date().toISOString());
  const [showEmployeeInvalidClubModal, setEmployeeInvalidClubModal] = useState(false);

  const getInitialResidenceAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      unitType: address?.unitType || '',
      unitNumber: address?.unitNumber || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
      googleAddressId: address?.googleAddressId || '',
    };
  };

  const getInitialMailingAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
    };
  };

  const tag = () => {
    sendTag(UtagType.view, pageNameTag);
    setIsTagging(false);
  };

  const [residenceAddressForm, setResidenceAddressFormData] = useState(
    getInitialResidenceAddressState(formData.residenceAddress)
  );

  const [residenceAddressSearchValue, setResidenceAddressSearchValue] = useState(() =>
    buildAddressSearchValue(formData.residenceAddress)
  );
  const [hasMailingAddress, setHasMailingAddress] = useState(
    formData.mailingAddress != undefined &&
      (formData.mailingAddress.streetAddress !== '' ||
        formData.mailingAddress?.city !== '' ||
        formData.mailingAddress?.state !== '' ||
        formData.mailingAddress?.zipCode !== '')
  );

  const [mailingAddressForm, setMailingAddressFormData] = useState(
    getInitialMailingAddressState(formData.mailingAddress)
  );
  const [mailingAddressSearchValue, setMailingAddressSearchValue] = useState(() =>
    buildAddressSearchValue(formData.mailingAddress)
  );

  useEffect(() => {
    const updatedResidenceFormData = {
      ...sessionData.formData,
      residenceAddress: {
        ...sessionData.formData.residenceAddress,
        ...residenceAddressForm,
      },
    };
    updateMembershipForm(updatedResidenceFormData);
  }, [residenceAddressForm]);

  useEffect(() => {
    const updatedMailingAddressFormData = {
      ...sessionData.formData,
      mailingAddress: {
        ...sessionData.formData.mailingAddress,
        ...mailingAddressForm,
      },
    };
    updateMembershipForm(updatedMailingAddressFormData);
  }, [mailingAddressForm]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    tag();
  }, [membershipInfo]);

  const showOutOfTerritoryError = () => {
    sendTag(UtagType.link, pageNameTag, {
      error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-out of territory`,
    });
    setServerValidationErrors({
      hasError: true,
      error: `Your zip code is not part of this club's coverage area.`,
    });
  };

  const handleOutOfTerritoryError = (isOutOfACETerritory: boolean, zipCode: string) => {
    const isMajorRocket = sessionData.flowType === FlowType.MAJOR_ROCKET;

    if (isMajorRocket) {
      showOutOfTerritoryError();
    } else if (isGiftFlow) {
      history.push({
        pathname: Pages.GiftRecipientClubMismatchPage,
        state: { isOutOfACETerritory, zipCode },
      });
    } else {
      setIsOOT(isOutOfACETerritory);
      setShowConfirmZipCodeModal(true);
    }
    loadingProvider.endLoading();
  };

  const navigateToNextPage = async () => {
    loadingProvider.startLoading('Saving your membership information');
    await getNewBusinessCost();
    loadingProvider.endLoading();
    history.push(getNextPage(currentPage, sessionData.flowType));
  };

  const progressProps = getProgressProps(sessionData.flowType!, currentPage);

  const clearSelectedAddress = () => {
    setAddressSelected(false);
    if (isGettingResidenceAddress) {
      setResidenceAddressSearchValue(null);
      setResidenceAddressFormData((prev) => ({
        ...prev,
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        googleAddressId: '',
      }));
      return;
    }
    setMailingAddressSearchValue(null);
    setMailingAddressFormData((prev) => ({
      ...prev,
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      googleAddressId: '',
    }));
  };

  const submitForm = async (props?: { skipApiAddressValidation?: boolean }) => {
    try {
      loadingProvider.startLoading('Saving your membership information');
      setServerValidationErrors({ hasError: false, error: '' });
      const response = await submitMembershipForm(true, true);
      const isOutOfACETerritory = response?.isOutOfACETerritory ?? false;
      const isEmployeeFlow = sessionData.flowType === FlowType.EMPLOYEE;

      if (response?.isActiveMember) {
        sendTag(UtagType.link, pageNameTag, {
          error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-current active member (VALD)`,
        });
        if (sessionData.flowType === FlowType.T_MOBILE) {
          history.push(Pages.MemberExists);
        } else if (sessionData.flowType === FlowType.GROUPON) {
          history.push(Pages.PartnerMemberExists);
        } else {
          setServerValidationErrors({
            hasError: true,
            error: 'An active Membership is associated with your name and address.',
          });
        }
        loadingProvider.endLoading();
      } else if (isEmployeeFlow && !response.isEmployeeClubValid) {
        setEmployeeInvalidClubModal(true);
        loadingProvider.endLoading();
      } else if (isGrouponFlow && !response.isGrouponClubValid) {
        history.push(Pages.OfferNotAvailable);
      } else if (isOutOfACETerritory || response?.isClubCodeMismatch) {
        //Check both OutOfTerritory && ClubCodeMismatch
        handleOutOfTerritoryError(isOutOfACETerritory, residenceAddressForm?.zipCode ?? '');
      } else if (hasMailingAddress && isGettingResidenceAddress) {
        history.push(Pages.MailingAddressPage);
      } else {
        navigateToNextPage();
      }
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;
      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            submitForm(props);
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };

  const clearResidenceAddressForm = () => {
    updateMembershipForm({
      ...formData,
      residenceAddress: getInitialResidenceAddressState({} as Address),
    });
  };

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  const unitTypeList = [
    { name: 'N/A', value: 'N/A' },
    { name: 'Apt', value: 'Apt' },
    { name: 'Unit', value: 'Unit' },
    { name: 'Suite', value: 'Suite' },
    { name: 'Floor', value: 'Floor' },
    { name: 'Room', value: 'Room' },
    { name: 'Building', value: 'Building' },
    { name: 'Other', value: 'Other' },
  ];

  return (
    <>
      <AppProgressHeader {...progressProps} />
      {loadingInfo.isLoading ? (
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={2} currentStep={loadingInfo.loadingDetails.length} />
      ) : (
        <>
          <EmployeeInvalidClubModal
            open={showEmployeeInvalidClubModal}
            onClose={() => {
              setEmployeeInvalidClubModal(false);
            }}
          />
          <Box className="auto-complete-address-header-container">
            <Typography variant="h5" className="auto-complete-address-header-title">
              {getAddressPageTitle(isGiftFlow, isGettingResidenceAddress)}
            </Typography>
            {serverValidationErrors?.hasError ? (
              <Alert
                children={
                  <Typography className="validation-error-alert" variant="b3Regular">
                    {serverValidationErrors?.error}
                  </Typography>
                }
                severity="error"
                displayCloseButton={false}
              />
            ) : isGiftFlow ? (
              <></>
            ) : (
              <Typography variant="b1Regular" className="auto-complete-address-header-text">
                We need your address to validate your Auto Club membership.
              </Typography>
            )}
          </Box>

          <Form sx={{ marginTop: 0 }}>
            <Box className="address-form qm-mask">
              <AddressSearch
                action={{
                  label: 'I do not see my address',
                  onClick: () => {
                    //clear unit info to avoid duplications after manual address entry
                    clearResidenceAddressForm();
                    history.push(isGettingResidenceAddress ? Pages.ResidenceAddressPage : Pages.MailingAddressPage);
                  },
                }}
                sx={{ marginTop: '1rem' }}
                value={isGettingResidenceAddress ? residenceAddressSearchValue : mailingAddressSearchValue}
                helperText=""
                placeholder="Type in"
                label="Address"
                className={isGettingResidenceAddress ? 'residence-address-search' : 'mailing-address-search'}
                id="address"
                fullWidth
                required
                data-qm-block="true"
                validator={addressValidator}
                onChange={(v) => {
                  setAddressSelected(true);
                  if (isGettingResidenceAddress && v != null) {
                    setResidenceAddressSearchValue((v as any) ?? null);
                    setResidenceAddressFormData((prev) => ({
                      ...prev,
                      googleAddressId: (v as any)?.id ?? '',
                      streetAddress: (v as any)?.longParts?.street ?? '',
                      city: (v as any)?.longParts?.city ?? '',
                      state: (v as any)?.shortParts?.state ?? '',
                      zipCode: (v as any)?.longParts?.zip ?? '',
                    }));
                    return;
                  }
                  if (v !== null) {
                    setMailingAddressSearchValue((v as any) ?? null);
                    setMailingAddressFormData((prev) => ({
                      ...prev,
                      googleAddressId: (v as any)?.id ?? '',
                      streetAddress: (v as any)?.longParts?.street ?? '',
                      city: (v as any)?.longParts?.city ?? '',
                      state: (v as any)?.shortParts?.state ?? '',
                      zipCode: (v as any)?.longParts?.zip ?? '',
                    }));
                  }
                }}
                onClearButtonClick={clearSelectedAddress}
              />
              <Grid container marginTop="1rem">
                <Grid size={6}>
                  <Select
                    label="Unit type"
                    //the dropdown will be full screen when number of items reaches 6(default threshold), so we set up a larger number here to avoid full screen dropdown
                    fullScreenThreshold={10}
                    fullWidth
                    name="unitType"
                    placeholder="Select"
                    displayEmpty
                    id="unitTypeDropdown"
                    helperText="Only if applicable"
                    value={unitTypeList.find((u) => {
                      if (isGettingResidenceAddress) {
                        return u.value === (residenceAddressForm.unitType as string);
                      }
                      return u.value === (mailingAddressForm.unitType as string);
                    })}
                    getDisplayText={(v: any) => {
                      return v.name;
                    }}
                    onChange={(v: any) => {
                      const x = v.target.value;
                      if (isGettingResidenceAddress) {
                        if (x.value === 'N/A') {
                          setResidenceAddressFormData((prev) => ({
                            ...prev,
                            unitNumber: '',
                          }));
                        }
                        setResidenceAddressFormData((prev) => ({
                          ...prev,
                          unitType: x.value,
                        }));
                        return;
                      }
                      setMailingAddressFormData((prev) => ({
                        ...prev,
                        unitType: x.value,
                      }));
                      if (x.value === 'N/A') {
                        setMailingAddressFormData((prev) => ({
                          ...prev,
                          unitNumber: '',
                        }));
                      }
                    }}
                  >
                    {unitTypeList.map((unitType, i) => (
                      <MenuItem key={unitType.name} value={unitType as any} id={unitType.value}>
                        {unitType.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid size={6}>
                  <TextField
                    value={
                      isGettingResidenceAddress
                        ? (residenceAddressForm.unitNumber ?? '')
                        : (mailingAddressForm.unitNumber ?? '')
                    }
                    label="Unit number"
                    disabled={
                      isGettingResidenceAddress
                        ? residenceAddressForm.unitType === 'N/A'
                        : mailingAddressForm.unitType === 'N/A'
                    }
                    id="unit-number"
                    name="unitNumber"
                    className="address-unit-number"
                    data-qm-block="true"
                    placeholder="e.g. 4B, 300, A"
                    fullWidth
                    onChange={(v) => {
                      const unitNumber = v.currentTarget.value;
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({
                          ...prev,
                          unitNumber,
                        }));
                        return;
                      }
                      setMailingAddressFormData((prev) => ({
                        ...prev,
                        unitNumber,
                      }));
                    }}
                    onClearButtonClick={() => {
                      isGettingResidenceAddress
                        ? setResidenceAddressFormData((prev) => ({ ...prev, unitNumber: '' }))
                        : setMailingAddressFormData((prev) => ({ ...prev, unitNumber: '' }));
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {isGettingResidenceAddress && (
              <Box className="check-items" id="checkbox-align">
                <Checkbox
                  key={formKey}
                  name="check-different-mailing"
                  value={hasMailingAddress}
                  label={differentAddressText}
                  id="check-different-mailing"
                  variant="text"
                  checked={hasMailingAddress}
                  onChange={(e) => {
                    setHasMailingAddress(e.checked);
                    setFormKey(new Date().toISOString());
                    if (!e.checked) {
                      setMailingAddressFormData(() => ({
                        streetAddress: '',
                        city: '',
                        state: '',
                        zipCode: '',
                      }));
                    }
                  }}
                  sx={{
                    '& .MuiCheckbox-FormControlLabel:focus-within': {
                      outline: 'transparent !important',
                      boxShadow: 'none !important',
                    },
                    '& .MuiCheckbox-root.Mui-focusVisible': {
                      outline: 'transparent !important',
                      boxShadow: 'none !important',
                    },
                  }}
                  fullWidth
                ></Checkbox>
              </Box>
            )}

            <Box className="auto-complete-address-form-btn">
              <Button
                label="Next"
                fullWidth
                onClick={() => {
                  submitForm();
                }}
                sx={{ marginTop: '1.5rem' }}
                type="submit"
                variant="contained"
                size="medium"
                id="nextButton"
              ></Button>
            </Box>
            <ConfirmZipCodeModal
              isOpen={showConfirmZipCodeModal}
              closeModal={() => {
                showOutOfTerritoryError();
                setShowConfirmZipCodeModal(false);
              }}
              confirm={() => {
                handleZipCodeChange(isOOT);
                setShowConfirmZipCodeModal(false);
              }}
            />
          </Form>
        </>
      )}
    </>
  );
};
